import { useMemo } from 'react';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import isDateInRange from '~/shared/components/DatePicker/DatePickerRange/helpers/isDateInRange';
import { useOrderHistoryTableState } from '~/state/useOrderHistoryState';

export default function useFilterOrderTableData(
    data: ShopTypes.OrderHeader[],
    dateRange: (Date | null)[],
) {
    const { translate } = useTranslations();
    const { query, selectedStatus } = useOrderHistoryTableState();

    const filteredData = useMemo(() => {
        if (!data) return [];

        if (!selectedStatus.length && !query && !dateRange[0] && !dateRange[1]) return data;

        const getDeliveryMethod = (method: ShopTypes.DeliveryMethod) => {
            switch (method) {
                case ShopTypes.DeliveryMethod.Pickup:
                    return translate('mit-fog.deliveryMethod.pickup');
                case ShopTypes.DeliveryMethod.Delivery:
                    return translate('mit-fog.deliveryMethod.delivery');
                case ShopTypes.DeliveryMethod.PickUpDirect:
                    return translate('mit-fog.deliveryMethod.pickupDirect');
                case ShopTypes.DeliveryMethod.Courier:
                    return translate('mit-fog.deliveryMethod.courier');
                case ShopTypes.DeliveryMethod.Mover:
                    return translate('mit-fog.deliveryMethod.mover');
                case ShopTypes.DeliveryMethod.DeliveryDirect:
                    return translate('mit-fog.deliveryMethod.deliveryDirect');
                case ShopTypes.DeliveryMethod.Postal:
                    return translate('mit-fog.deliveryMethod.postal');
                case ShopTypes.DeliveryMethod.PostalBusiness:
                    return translate('mit-fog.deliveryMethod.postalBusiness');
            }
        };

        let filteredData = data;

        if (dateRange[0] && dateRange[1]) {
            filteredData = filteredData.filter((item) => isDateInRange(item.orderDate, dateRange));
        }

        if (query.length) {
            filteredData = filteredData.filter((item) => {
                const includesDeliveryAddress = item.deliveryAddress?.addressLine1
                    ?.toLocaleLowerCase()
                    .includes(query.toLocaleLowerCase());
                const includesOrderId = item.orderId
                    ?.toLocaleLowerCase()
                    .includes(query.toLocaleLowerCase());
                const includesCustomersReference = item.customersReference
                    ?.toLocaleLowerCase()
                    .includes(query.toLocaleLowerCase());
                const includesDeliveryMethod = getDeliveryMethod(item.deliveryMethod)
                    ?.toLocaleLowerCase()
                    .includes(query.toLocaleLowerCase());

                if (
                    includesDeliveryAddress ||
                    includesOrderId ||
                    includesCustomersReference ||
                    includesDeliveryMethod
                ) {
                    return item;
                }
            });
        }

        if (selectedStatus.length) {
            filteredData = filteredData.filter((item) =>
                selectedStatus.find(
                    (status) =>
                        (Number(status.value) as ShopTypes.OrderStatus.Invoiced) === item.status,
                ),
            );
        }

        return filteredData;
    }, [data, query, selectedStatus, dateRange, translate]);

    return {
        query,
        filteredData,
        selectedStatus,
    };
}
